import { Button, Grid, ScrollView, SearchField } from "@aws-amplify/ui-react";
import { useMemo, useState } from "react";
import LocalizedStrings from "react-localization";
import moment from "moment";
import {
  CCard,
  CCardBody,
  CCollapse,
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
  CTable,
} from "@coreui/react";

import ConfirmModal from "components/utils/ConfirmModal";
import ActionIcon from "components/actions/ActionIcon";
import demoCropVariables from "config/demo/cropVariables.json";
import CropVariableCreateForm from "./CropVariableCreateForm";
import CropVariableUpdateForm from "./CropVariableUpdateForm";

const CropVariables = () => {
  const [variables] = useState(demoCropVariables.slice(0, 10));
  const [variableDetails, setVariableDetails] = useState();
  const [modalDetails, setModalDetails] = useState({
    visible: false,
    itemId: null,
    itemName: null,
  });
  const [modalForm, setModalForm] = useState({
    visible: false,
    itemId: null,
    action: null,
  });

  const columnsMapped = useMemo(
    () =>
      variables.map((item) => ({
        ...item,
        actions: (
          <>
            <ActionIcon
              name={
                variableDetails?.id === item.id
                  ? strings.variables.hide
                  : strings.variables.show
              }
              icon={
                variableDetails?.id === item.id ? "VisibilityOff" : "Visibility"
              }
              color="primary"
              onClick={() => {
                variableDetails?.id === item.id
                  ? setVariableDetails(null)
                  : setVariableDetails(item);
              }}
            />
            <ActionIcon
              name={strings.variables.edit}
              icon="Edit"
              color="primary"
              onClick={() => openModal("update", item.id)}
            />
            <ActionIcon
              name={strings.variables.delete}
              icon="Delete"
              color="red.60"
              onClick={() =>
                setModalDetails({
                  visible: true,
                  itemId: item.id,
                  itemName: item.name,
                })
              }
            />
          </>
        ),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [variables, variableDetails?.id]
  );

  const openModal = (action, id = null) =>
    setModalForm({ visible: true, action, itemId: id });

  const tableColumns = [
    {
      key: "name",
      label: strings.table.headers.name,
      _style: { width: "45%" },
    },
    {
      key: "measurementUnit",
      label: strings.table.headers.measurementUnit,
      _style: { width: "25%" },
    },
    {
      key: "createdAt",
      label: strings.table.headers.createdAt,
      _style: { width: "15%" },
    },
    {
      key: "actions",
      label: "",
      _style: { width: "15%" },
    },
  ];

  return (
    <div>
      <span className="fs-3">{strings.title}</span>
      <Button
        style={styles.buttonAdd}
        children={strings.variables.addVariable}
        variation="primary"
        onClick={() => openModal("create")}
      />
      <div className="p-3">
        <Grid
          className="mb-3"
          templateColumns={{ medium: "1fr", xl: "1fr 1fr 1fr" }}
          rowGap="15px"
          columnGap="15px"
        >
          <SearchField
            label={strings.table.search.label}
            placeholder={strings.table.search.placeholder}
          />
        </Grid>

        <CTable
          className="table-centered"
          items={columnsMapped}
          columns={tableColumns}
          tableHeadProps={{ color: "light" }}
          responsive
          hover
        />

        <CCollapse visible={!!variableDetails?.id} style={styles.collapseCard}>
          <CCard key={variableDetails?.id}>
            <CCardBody>
              <h4 className="text-muted">
                {strings.details.title} "{variableDetails?.name}"
              </h4>

              <p className="text-muted">
                <b>{strings.details.name}:</b> {variableDetails?.name}
              </p>

              <p className="text-muted">
                <b>{strings.details.measurementUnit}:</b>{" "}
                {variableDetails?.measurementUnit}
              </p>

              <p className="text-muted">
                <b>{strings.details.createdAt}:</b>{" "}
                {variableDetails?.createdAt
                  ? moment(variableDetails?.createdAt).format(
                      "YYYY-MM-DD HH:mm:ss"
                    )
                  : "-"}
              </p>
            </CCardBody>
          </CCard>
        </CCollapse>
      </div>

      <ConfirmModal
        visible={modalDetails.visible}
        body={strings.formatString(strings.modal.confirmDeleteQuestion, {
          name: modalDetails.itemName,
        })}
        onCancel={() => setModalDetails({ visible: false, itemId: null })}
        // onConfirm={() => deleteCropVariable(modalDetails.itemId)}
      />

      {modalForm.action && (
        <CModal
          size="xl"
          alignment="center"
          visible={modalForm.visible}
          onClose={() => setModalForm({ visible: false })}
          backdrop="static"
        >
          <CModalHeader>
            <CModalTitle>
              {modalForm.action === "create" &&
                strings.modalForm.addTitle}
              {modalForm.action === "update" &&
                strings.modalForm.updateTitle}
            </CModalTitle>
          </CModalHeader>
          <CModalBody style={{ overflow: "hidden" }}>
            <ScrollView maxHeight="75vh">
              {modalForm.action === "create" && (
                <CropVariableCreateForm onSuccess={() => {}} />
              )}
              {modalForm.action === "update" && (
                <CropVariableUpdateForm
                  cropVariableId={modalForm.itemId}
                  onSuccess={() => {}}
                />
              )}
            </ScrollView>
          </CModalBody>
        </CModal>
      )}
    </div>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Variables Programación Cultivo",
    variables: {
      addVariable: "Agregar Variable",
      show: "Ver",
      hide: "Ocultar",
      edit: "Editar",
      delete: "Eliminar",
    },
    table: {
      headers: {
        name: "Nombre",
        measurementUnit: "Unidad de Medida",
        createdAt: "Fecha de creación",
      },
      search: {
        label: "Buscar",
        placeholder: "Buscar por palabra clave...",
      },
    },
    details: {
      title: "Detalles de la Variable",
      name: "Nombre",
      measurementUnit: "Unidad de Medida",
      createdAt: "Fecha de creación",
    },
    modal: {
      confirmDeleteQuestion: "¿Estás seguro de eliminar la variable {name}?",
    },
    modalForm: {
      addTitle: "Agregar Variable",
      updateTitle: "Actualizar Variable",
    },
  },
});

const styles = {
  buttonAdd: { float: "right" },
  collapseCard: { marginTop: "15px" },
};

export default CropVariables;
